import React, { ReactNode } from 'react';

export type SpanCell = {
  __spanCell: true;
  content: ReactNode;
  colSpan?: number;
  className?: string;
};

export function spanCell(
  content: ReactNode,
  options?: { colSpan?: number; className?: string },
): SpanCell {
  return { __spanCell: true, content, ...options };
}

function isSpanCell(cell: ReactNode | SpanCell): cell is SpanCell {
  return (
    typeof cell === 'object' &&
    cell !== null &&
    '__spanCell' in cell &&
    (cell as SpanCell).__spanCell === true
  );
}

type Row = {
  data: (ReactNode | SpanCell)[];
  key?: string;
  className?: string;
  cellClassName?: string;
};

export function Table({
  headings,
  rows,
  columnWidths,
  tableLayout = 'fixed',
}: {
  headings: ReactNode[];
  rows: Row[];
  columnWidths?: string[];
  tableLayout?: 'auto' | 'fixed';
}) {
  return (
    <div className="overflow-x-auto">
      <table
        className={`w-full text-left border-collapse ${tableLayout === 'auto' ? 'table-auto' : 'table-fixed'}`}
      >
        <thead className="bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 border-b p-2 divide-y divide-gray-200 dark:divide-gray-800">
          <tr className="text-sm font-semibold divide-x dark:divide-gray-800">
            {headings.map((heading, index) => (
              <th
                key={index}
                style={{ width: columnWidths?.[index] }}
                className="p-3 whitespace-nowrap text-black dark:text-gray-200"
              >
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {rows.map((row, i1) => (
            <tr key={row.key ?? i1} className={row.className}>
              {row.data.map((cell, i2) =>
                isSpanCell(cell) ? (
                  <td
                    key={i2}
                    colSpan={cell.colSpan}
                    className={`text-sm text-gray-800 dark:text-gray-200 p-3 break-words ${cell.className ?? ''} ${row.cellClassName ?? ''}`}
                  >
                    {cell.content}
                  </td>
                ) : (
                  <td
                    key={i2}
                    style={{ width: columnWidths?.[i2] }}
                    className={`text-sm text-gray-800 dark:text-gray-200 p-3 break-words ${row.cellClassName ?? ''}`}
                  >
                    {cell}
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
